<template>
  <div class="doc-layout">

    <!-- ── Sidebar ───────────────────────────────────────────────────────── -->
    <aside class="doc-sidebar" :class="{ 'doc-sidebar-open': sidebarOpen }">
      <div class="doc-sidebar-header">
        <div class="doc-logo">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#E86A10" stroke-width="2" stroke-linecap="round"/>
            <polyline points="14 2 14 8 20 8" stroke="#E86A10" stroke-width="2" stroke-linecap="round"/>
            <line x1="16" y1="13" x2="8" y2="13" stroke="#E86A10" stroke-width="2" stroke-linecap="round"/>
            <line x1="16" y1="17" x2="8" y2="17" stroke="#E86A10" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <span>Documentação</span>
        </div>

        <!-- Busca -->
        <div class="doc-search-wrap">
          <svg class="doc-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            v-model="search"
            class="doc-search-input"
            placeholder="Buscar artigos..."
            @input="onSearch"
          />
          <button v-if="search" class="doc-search-clear" @click="clearSearch">×</button>
        </div>
      </div>

      <!-- Resultados da busca -->
      <div v-if="search" class="doc-search-results">
        <div v-if="searchResults.length === 0" class="doc-no-results">
          Nenhum artigo encontrado
        </div>
        <button
          v-for="r in searchResults"
          :key="r.id"
          class="doc-search-item"
          @click="openArticle(r)"
        >
          <span class="doc-search-item-cat">{{ r.categoryLabel }}</span>
          <span class="doc-search-item-title">{{ r.title }}</span>
        </button>
      </div>

      <!-- Categorias e artigos -->
      <nav v-else class="doc-nav">
        <div
          v-for="cat in categories"
          :key="cat.id"
          class="doc-nav-category"
        >
          <button
            class="doc-nav-cat-btn"
            :class="{ 'doc-cat-active': openCats[cat.id] }"
            @click="toggleCat(cat.id)"
          >
            <span class="doc-cat-icon" v-html="cat.icon"></span>
            <span class="doc-cat-label">{{ cat.label }}</span>
            <svg class="doc-cat-chevron" :class="{ 'rotated': openCats[cat.id] }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
          </button>

          <div v-show="openCats[cat.id]" class="doc-nav-articles">
            <button
              v-for="art in cat.articles"
              :key="art.id"
              class="doc-nav-article"
              :class="{ 'doc-art-active': activeArticle && activeArticle.id === art.id }"
              @click="openArticle(art, cat)"
            >
              {{ art.title }}
            </button>
          </div>
        </div>
      </nav>
    </aside>

    <!-- ── Conteúdo principal ─────────────────────────────────────────────── -->
    <main class="doc-main">

      <!-- Mobile header -->
      <div class="doc-mobile-header">
        <button class="doc-menu-btn" @click="sidebarOpen = !sidebarOpen">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        <span class="doc-mobile-title">Documentação</span>
      </div>

      <!-- Home / sem artigo selecionado -->
      <div v-if="!activeArticle" class="doc-home">

        <div class="doc-home-hero">
          <h1 class="doc-home-title">Central de Ajuda <span>VSLBooster</span></h1>
          <p class="doc-home-sub">Tudo o que você precisa para criar, configurar e escalar seus VSLs com máxima conversão.</p>
          <div class="doc-home-search-wrap">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              v-model="search"
              class="doc-home-search"
              placeholder="O que você está procurando?"
              @input="onSearch"
            />
          </div>
        </div>

        <!-- Cards de categorias -->
        <div class="doc-cat-grid">
          <button
            v-for="cat in categories"
            :key="cat.id"
            class="doc-cat-card"
            @click="toggleCat(cat.id, true)"
          >
            <div class="doc-cat-card-icon" v-html="cat.icon"></div>
            <div class="doc-cat-card-info">
              <div class="doc-cat-card-label">{{ cat.label }}</div>
              <div class="doc-cat-card-count">{{ cat.articles.length }} artigo{{ cat.articles.length !== 1 ? 's' : '' }}</div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>

        <!-- Artigos populares -->
        <div class="doc-popular">
          <h2 class="doc-section-title">Artigos populares</h2>
          <div class="doc-popular-grid">
            <button
              v-for="art in popularArticles"
              :key="art.id"
              class="doc-popular-card"
              @click="openArticle(art)"
            >
              <div class="doc-popular-tag">{{ art.categoryLabel }}</div>
              <div class="doc-popular-title">{{ art.title }}</div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Artigo aberto -->
      <article v-else class="doc-article">

        <!-- Breadcrumb -->
        <div class="doc-breadcrumb">
          <button class="doc-bc-home" @click="goHome">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            Início
          </button>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
          <span class="doc-bc-cat">{{ activeCategory ? activeCategory.label : '' }}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
          <span class="doc-bc-art">{{ activeArticle.title }}</span>
        </div>

        <!-- Header do artigo -->
        <div class="doc-art-header">
          <div class="doc-art-tags">
            <span v-for="tag in activeArticle.tags" :key="tag" class="doc-tag">{{ tag }}</span>
          </div>
          <h1 class="doc-art-title">{{ activeArticle.title }}</h1>
          <div class="doc-art-meta">
            <span>Última atualização: {{ activeArticle.updated }}</span>
            <span class="doc-art-dot">·</span>
            <span>{{ activeArticle.readTime }} min de leitura</span>
          </div>
        </div>

        <!-- Corpo do artigo (renderizado) -->
        <div class="doc-art-body" v-html="activeArticle.content"></div>

        <!-- Navegação prev/next -->
        <div class="doc-art-nav">
          <button v-if="prevArticle" class="doc-nav-btn doc-nav-prev" @click="openArticle(prevArticle)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
            <div>
              <div class="doc-nav-btn-label">Anterior</div>
              <div class="doc-nav-btn-title">{{ prevArticle.title }}</div>
            </div>
          </button>
          <div v-else></div>
          <button v-if="nextArticle" class="doc-nav-btn doc-nav-next" @click="openArticle(nextArticle)">
            <div>
              <div class="doc-nav-btn-label">Próximo</div>
              <div class="doc-nav-btn-title">{{ nextArticle.title }}</div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>

        <!-- Feedback -->
        <div class="doc-feedback">
          <div class="doc-feedback-title">Este artigo foi útil?</div>
          <div class="doc-feedback-btns">
            <button class="doc-fb-btn" :class="{ active: feedback === 'yes' }" @click="feedback = 'yes'">
              👍 Sim
            </button>
            <button class="doc-fb-btn" :class="{ active: feedback === 'no' }" @click="feedback = 'no'">
              👎 Não
            </button>
          </div>
          <div v-if="feedback === 'yes'" class="doc-feedback-thanks">
            Obrigado pelo feedback! 🎉
          </div>
          <div v-if="feedback === 'no'" class="doc-feedback-thanks">
            Lamentamos! Entre em contato com o suporte para mais ajuda.
          </div>
        </div>
      </article>
    </main>

    <!-- Overlay mobile -->
    <div v-if="sidebarOpen" class="doc-overlay" @click="sidebarOpen = false" />
  </div>
</template>

<script>
const CATEGORIES = [
  /* ═══════════════════════════════════════════════════════════════════════
     1. PRIMEIROS PASSOS
  ═══════════════════════════════════════════════════════════════════════ */
  {
    id: 'primeiros-passos',
    label: 'Primeiros Passos',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2" stroke-linecap="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`,
    articles: [
      {
        id: 'o-que-e-vslbooster',
        title: 'O que é o VSLBooster',
        tags: ['introdução', 'overview'],
        updated: 'Mar 2025',
        readTime: 3,
        categoryLabel: 'Primeiros Passos',
        popular: true,
        content: `
          <h2>O que é o VSLBooster?</h2>
          <p>O VSLBooster é uma plataforma completa para criação, hospedagem e otimização de <strong>Video Sales Letters (VSLs)</strong>. Com ele você consegue:</p>
          <ul>
            <li>Hospedar seus vídeos de vendas sem depender do YouTube ou Vimeo</li>
            <li>Personalizar o player com cores, CTAs e barras de progresso inteligentes</li>
            <li>Acompanhar analytics em tempo real: quem assistiu, até onde chegou e quantos converteram</li>
            <li>Integrar com ferramentas de automação de marketing</li>
            <li>Criar webinários simulados com chat interativo</li>
            <li>Analisar o engajamento com Mapa de Calor</li>
          </ul>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 Dica:</strong> Comece criando seu primeiro vídeo no menu <em>Meus Videos</em> e depois personalize o player.
          </div>
          <h2>Conceitos fundamentais</h2>
          <p>Antes de começar, entenda os pilares do VSLBooster:</p>
          <div class="doc-cards-inline">
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">🎬 Player</div>
              <div>O player é o componente de vídeo embutido na sua página. Ele controla aparência, comportamento e CTAs.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">📊 Analytics</div>
              <div>Métricas detalhadas de visualização, retenção e conversão para cada vídeo.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">💬 ChatBooster</div>
              <div>Chat interativo com fluxos automatizados para captura e nutrição de leads.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">🔗 Integrações</div>
              <div>Conecte com ActiveCampaign, RD Station, PipeDrive e mais via webhooks.</div>
            </div>
          </div>
        `,
      },
      {
        id: 'criando-primeiro-video',
        title: 'Criando seu primeiro vídeo',
        tags: ['vídeo', 'upload', 'início'],
        updated: 'Mar 2025',
        readTime: 5,
        categoryLabel: 'Primeiros Passos',
        popular: true,
        content: `
          <h2>Como criar seu primeiro vídeo</h2>
          <p>Siga este passo a passo para ter seu VSL no ar em menos de 10 minutos.</p>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">
                <strong>Acesse "Meus Videos"</strong>
                <p>No menu lateral, clique em <em>Meus Videos</em>. Você verá todos os seus vídeos já cadastrados.</p>
              </div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">
                <strong>Clique em "Novo Vídeo"</strong>
                <p>Preencha o título, faça o upload do arquivo ou cole o link do vídeo (S3, CDN própria).</p>
              </div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">
                <strong>Configure o Player</strong>
                <p>Personalize cor da barra de progresso, thumbnail, e defina se haverá CTA (Call to Action).</p>
              </div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">4</div>
              <div class="doc-step-content">
                <strong>Copie o Código de Embed</strong>
                <p>Use o snippet gerado para inserir o player na sua landing page ou funil.</p>
              </div>
            </div>
          </div>
          <div class="doc-callout doc-callout-warning">
            <strong>⚠️ Atenção:</strong> O arquivo de vídeo deve estar em formato MP4, resolução máxima de 1080p para melhor performance.
          </div>
        `,
      },
      {
        id: 'embed-player',
        title: 'Como embutir o player no seu site',
        tags: ['embed', 'código', 'html'],
        updated: 'Mar 2025',
        readTime: 4,
        categoryLabel: 'Primeiros Passos',
        content: `
          <h2>Embutindo o player VSLBooster</h2>
          <p>Após configurar seu vídeo, o VSLBooster gera um snippet de código para você colar em qualquer página HTML.</p>
          <h3>Snippet padrão</h3>
          <div class="doc-code-block">
<pre>&lt;div id="vsl-player-SEUID"&gt;&lt;/div&gt;
&lt;script src="https://seudominio.com/js/booster.min.js"&gt;&lt;/script&gt;
&lt;script&gt;
  vslBooster.init({
    videoId: 'SEUID',
    container: '#vsl-player-SEUID'
  });
&lt;/script&gt;</pre>
          </div>
          <p>Substitua <code>SEUID</code> pelo ID do seu vídeo, disponível na tela de detalhes do vídeo.</p>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 WordPress:</strong> Cole o snippet em um bloco HTML customizado. Não use o editor visual — ele pode remover as tags <code>&lt;script&gt;</code>.
          </div>
        `,
      },
      {
        id: 'script-universal',
        title: 'Script universal de incorporação',
        tags: ['embed', 'script', 'instalação'],
        updated: 'Abr 2025',
        readTime: 3,
        categoryLabel: 'Primeiros Passos',
        content: `
          <h2>Script universal</h2>
          <p>O VSLBooster oferece um <strong>script único</strong> que carrega tanto o player de vídeo quanto o chat no seu site. Com ele, você não precisa adicionar scripts separados para cada funcionalidade.</p>
          <h3>Como obter seu script</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Clique no seu <strong>avatar</strong> no canto superior direito da plataforma</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Selecione <strong>"Script"</strong> no menu dropdown</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Copie o código exibido e cole antes do <code>&lt;/body&gt;</code> do seu site</div>
            </div>
          </div>
          <h3>O que o script universal faz</h3>
          <ul>
            <li><strong>Chat:</strong> Exibe o widget de chat no canto inferior direito automaticamente</li>
            <li><strong>Player:</strong> Permite embedar vídeos usando shortcodes como <code>[vid_XXXXX]</code> em qualquer ponto da página</li>
          </ul>
          <div class="doc-callout doc-callout-info">
            <strong>ℹ️ Importante:</strong> O script é vinculado à sua conta via UUID. Cada conta possui um script exclusivo.
          </div>
        `,
      },
    ],
  },

  /* ═══════════════════════════════════════════════════════════════════════
     2. PLAYER DE VÍDEO
  ═══════════════════════════════════════════════════════════════════════ */
  {
    id: 'player',
    label: 'Player de Vídeo',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2" stroke-linecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>`,
    articles: [
      {
        id: 'barra-progresso',
        title: 'Personalizando a barra de progresso',
        tags: ['player', 'design', 'barra'],
        updated: 'Fev 2025',
        readTime: 3,
        categoryLabel: 'Player de Vídeo',
        popular: true,
        content: `
          <h2>Barra de progresso inteligente</h2>
          <p>A barra de progresso do VSLBooster não é só visual — ela é uma poderosa ferramenta de conversão.</p>
          <h3>Configurações disponíveis</h3>
          <div class="doc-table-wrap">
            <table class="doc-table">
              <thead><tr><th>Campo</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr><td><code>bar_cor</code></td><td>Cor hexadecimal da barra (ex: <code>#E86A10</code>)</td></tr>
                <tr><td><code>bar_hight</code></td><td>Espessura da barra em pixels (padrão: 6)</td></tr>
                <tr><td><code>id_vsl</code></td><td>ID do vídeo ao qual a barra pertence</td></tr>
              </tbody>
            </table>
          </div>
          <h3>Como configurar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse o vídeo em <em>Meus Videos</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Clique em <em>Configurações do Player</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Ajuste a cor e a espessura da barra</div>
            </div>
          </div>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 Dica de conversão:</strong> Use a cor da sua marca ou a cor do botão de compra para criar continuidade visual e aumentar cliques.
          </div>
        `,
      },
      {
        id: 'thumbnail',
        title: 'Configurando o thumbnail',
        tags: ['player', 'thumbnail', 'imagem'],
        updated: 'Fev 2025',
        readTime: 2,
        categoryLabel: 'Player de Vídeo',
        content: `
          <h2>Thumbnail do player</h2>
          <p>O thumbnail é a imagem exibida antes do usuário clicar no play. Uma boa imagem de capa aumenta significativamente a taxa de play.</p>
          <h3>Como configurar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse o vídeo e clique em <em>Thumbnails</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Clique em <em>Criar Thumbnail</em> para abrir o editor visual</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Faça upload de uma imagem ou use o editor para criar do zero</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">4</div>
              <div class="doc-step-content">Salve e ative o thumbnail criado</div>
            </div>
          </div>
          <h3>Boas práticas</h3>
          <ul>
            <li><strong>Dimensão recomendada:</strong> 1280×720px (proporção 16:9)</li>
            <li><strong>Formato:</strong> JPG ou PNG, máximo 500KB</li>
            <li><strong>Conteúdo:</strong> Rosto humano ou resultado visual — aumenta CTR em até 35%</li>
            <li><strong>Evite:</strong> Thumbnails com muito texto — ilegível em dispositivos móveis</li>
          </ul>
        `,
      },
      {
        id: 'cta-overlay',
        title: 'CTA Overlay — Botão de compra no vídeo',
        tags: ['player', 'CTA', 'conversão', 'botões'],
        updated: 'Mar 2025',
        readTime: 4,
        categoryLabel: 'Player de Vídeo',
        popular: true,
        content: `
          <h2>CTA Overlay e Botões de Ação</h2>
          <p>O CTA (Call to Action) Overlay exibe um botão ou elemento de conversão em cima do vídeo em um momento específico — geralmente após o pitch de vendas.</p>
          <h3>Como funciona</h3>
          <p>Você define o <strong>tempo em segundos</strong> em que o botão deve aparecer. O player automaticamente exibe o overlay naquele momento e o mantém até o fim do vídeo.</p>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse o vídeo e clique em <em>Botões e Ações</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content"><strong>Defina o tempo de aparecimento</strong> — recomendamos entre 70% e 85% do vídeo</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content"><strong>Configure o texto, cor e URL</strong> do botão CTA</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">4</div>
              <div class="doc-step-content"><strong>Teste a visualização</strong> — avance o vídeo até o ponto configurado</div>
            </div>
          </div>
          <div class="doc-callout doc-callout-tip">
            <strong>📈 Resultado médio:</strong> VSLs com CTA bem configurado convertem 2-4x mais do que com botão apenas abaixo do vídeo.
          </div>
        `,
      },
      {
        id: 'estilo-player',
        title: 'Estilo e customização visual do player',
        tags: ['player', 'estilo', 'design', 'customização'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'Player de Vídeo',
        content: `
          <h2>Customização visual do player</h2>
          <p>O VSLBooster permite personalizar completamente a aparência do player para combinar com a identidade visual da sua marca.</p>
          <h3>Como acessar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse o vídeo em <em>Meus Videos</em> e clique em <em>Customização</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Na aba <em>Estilo</em>, clique em <em>Criar Estilo</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Personalize cores, bordas, sombras e comportamento do player</div>
            </div>
          </div>
          <h3>Elementos customizáveis</h3>
          <ul>
            <li><strong>Cor de fundo e bordas</strong> — Defina o visual do container do player</li>
            <li><strong>Botão de play</strong> — Altere cor, tamanho e formato do botão central</li>
            <li><strong>Controles do player</strong> — Personalize a barra de controles (volume, fullscreen, etc.)</li>
            <li><strong>Barra de progresso</strong> — Cor, espessura e estilo da barra (veja artigo dedicado)</li>
          </ul>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 Dica:</strong> Você pode criar múltiplos estilos e alternar entre eles a qualquer momento. O preview em tempo real mostra as mudanças instantaneamente.
          </div>
        `,
      },
      {
        id: 'autoplay-hibrido',
        title: 'AutoPlay Híbrido',
        tags: ['player', 'autoplay', 'conversão'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'Player de Vídeo',
        content: `
          <h2>AutoPlay Híbrido</h2>
          <p>O AutoPlay Híbrido é uma técnica avançada que contorna as restrições dos navegadores contra autoplay com áudio. Ele inicia a reprodução do vídeo automaticamente <strong>sem som</strong>, exibindo um convite visual para o usuário ativar o áudio.</p>
          <h3>Como funciona</h3>
          <ul>
            <li>O vídeo começa a reproduzir automaticamente quando a página carrega (mutado)</li>
            <li>Um overlay animado convida o usuário a clicar para ativar o som</li>
            <li>Ao clicar, o áudio é ativado e a reprodução continua do ponto onde parou</li>
          </ul>
          <h3>Configuração</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse o vídeo e clique em <em>AutoPlay Híbrido</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Clique em <em>Criar AutoPlay</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Configure o texto e visual do overlay de ativação de áudio</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">4</div>
              <div class="doc-step-content">Ative e salve a configuração</div>
            </div>
          </div>
          <div class="doc-callout doc-callout-tip">
            <strong>📈 Impacto:</strong> VSLs com AutoPlay Híbrido aumentam a taxa de play em até 40%, pois o visitante já vê o vídeo rodando ao entrar na página.
          </div>
        `,
      },
      {
        id: 'continuar-assistindo',
        title: 'Continuar Assistindo',
        tags: ['player', 'retenção', 'continuação'],
        updated: 'Abr 2025',
        readTime: 3,
        categoryLabel: 'Player de Vídeo',
        content: `
          <h2>Continuar Assistindo</h2>
          <p>Este recurso <strong>memoriza o ponto onde o visitante parou</strong> de assistir o vídeo. Quando ele retorna à página, a reprodução continua exatamente de onde parou.</p>
          <h3>Como funciona</h3>
          <ul>
            <li>O progresso do visitante é salvo automaticamente no navegador (localStorage)</li>
            <li>Ao retornar, o player exibe um aviso "Continuar de onde parou?" com o tempo salvo</li>
            <li>O visitante pode continuar ou começar do início</li>
          </ul>
          <h3>Configuração</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse o vídeo e clique em <em>Continuar Assistindo</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Clique em <em>Criar</em> e personalize o texto e visual do aviso</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Ative o recurso e salve</div>
            </div>
          </div>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 Por que usar:</strong> VSLs longas (30min+) se beneficiam muito. Reduz a taxa de abandono em até 25%, pois visitantes que saíram no meio voltam e concluem a visualização.
          </div>
        `,
      },
      {
        id: 'progresso-inteligente',
        title: 'Progresso Inteligente',
        tags: ['player', 'progresso', 'fake-bar'],
        updated: 'Abr 2025',
        readTime: 3,
        categoryLabel: 'Player de Vídeo',
        content: `
          <h2>Progresso Inteligente</h2>
          <p>O Progresso Inteligente permite <strong>controlar o comportamento da barra de progresso</strong> para evitar que o espectador pule partes do vídeo.</p>
          <h3>Modos disponíveis</h3>
          <div class="doc-cards-inline">
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">🚫 Ocultar barra</div>
              <div>Esconde completamente a barra de progresso até o momento que você definir.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">🔒 Travar avanço</div>
              <div>O espectador pode ver a barra, mas não consegue avançar além do que já assistiu.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">⏱️ Revelação gradual</div>
              <div>A barra só aparece após o vídeo atingir determinado % de reprodução.</div>
            </div>
          </div>
          <h3>Configuração</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse o vídeo e clique em <em>Progresso Inteligente</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Escolha o modo desejado e configure os parâmetros</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Ative e salve</div>
            </div>
          </div>
          <div class="doc-callout doc-callout-warning">
            <strong>⚠️ Atenção:</strong> Usar modos restritivos em vídeos curtos (<5min) pode frustrar o visitante. Recomendado para VSLs acima de 15 minutos.
          </div>
        `,
      },
    ],
  },

  /* ═══════════════════════════════════════════════════════════════════════
     3. ANALYTICS E MAPA DE CALOR
  ═══════════════════════════════════════════════════════════════════════ */
  {
    id: 'analytics',
    label: 'Analytics e Mapa de Calor',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2" stroke-linecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>`,
    articles: [
      {
        id: 'entendendo-metricas',
        title: 'Entendendo as métricas do dashboard',
        tags: ['analytics', 'métricas', 'dashboard'],
        updated: 'Mar 2025',
        readTime: 6,
        categoryLabel: 'Analytics e Mapa de Calor',
        popular: true,
        content: `
          <h2>Métricas do Dashboard</h2>
          <p>O dashboard do VSLBooster é dividido em dois modos: <strong>Chat</strong> e <strong>Video</strong>. Alterne entre eles usando os botões no topo. Você pode filtrar por vídeo/chat específico e por período (Hoje, Ontem, 7 dias, 30 dias, Tudo ou intervalo personalizado).</p>
          <h3>KPIs principais</h3>
          <div class="doc-table-wrap">
            <table class="doc-table">
              <thead><tr><th>Métrica</th><th>O que mede</th><th>Referência boa</th></tr></thead>
              <tbody>
                <tr><td><strong>Visualizações</strong></td><td>Visitantes que carregaram a página com o player</td><td>–</td></tr>
                <tr><td><strong>Plays / Iniciado</strong></td><td>Quantos deram play (vídeo) ou iniciaram o chat</td><td>&gt; 60% das views</td></tr>
                <tr><td><strong>Play Rate / Finalizado</strong></td><td>Taxa de play (vídeo) ou quantos finalizaram (chat)</td><td>&gt; 50%</td></tr>
                <tr><td><strong>Engajamento</strong></td><td>% de interação geral com o conteúdo</td><td>&gt; 15%</td></tr>
              </tbody>
            </table>
          </div>
          <h3>Gráfico de funil</h3>
          <p>Abaixo dos KPIs, o gráfico de funil mostra a evolução das métricas visualmente, permitindo identificar onde os visitantes estão desistindo no processo.</p>
        `,
      },
      {
        id: 'retencao',
        title: 'Analisando a retenção do vídeo',
        tags: ['analytics', 'retenção', 'drop-off'],
        updated: 'Fev 2025',
        readTime: 5,
        categoryLabel: 'Analytics e Mapa de Calor',
        content: `
          <h2>Análise de retenção</h2>
          <p>A curva de retenção mostra em que momento os espectadores abandonam o vídeo. É a métrica mais valiosa para otimizar seu VSL.</p>
          <h3>Como interpretar</h3>
          <ul>
            <li><strong>Queda acentuada nos primeiros 30s:</strong> O hook (abertura) não está prendendo. Reformule a promessa inicial.</li>
            <li><strong>Queda gradual constante:</strong> Normal para VSLs longos. Aceito se conversão estiver boa.</li>
            <li><strong>Queda brusca em um ponto específico:</strong> Há um momento ruim no script — identifique e revise aquele trecho.</li>
            <li><strong>Subida próxima ao fim:</strong> Pessoas pulando para o final — coloque o CTA antes desse ponto.</li>
          </ul>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 Regra prática:</strong> VSLs com &gt; 40% de retenção média já estão bem. Acima de 60% é excelente.
          </div>
        `,
      },
      {
        id: 'analytics-video',
        title: 'Analytics individual do vídeo',
        tags: ['analytics', 'vídeo', 'relatório'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'Analytics e Mapa de Calor',
        content: `
          <h2>Analytics por vídeo</h2>
          <p>Além do dashboard geral, cada vídeo possui sua própria tela de analytics com dados detalhados.</p>
          <h3>Como acessar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Vá em <em>Meus Videos</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Clique no vídeo desejado para abrir o editor</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Acesse a aba <em>Analytics</em></div>
            </div>
          </div>
          <h3>Dados disponíveis</h3>
          <ul>
            <li><strong>Visualizações e plays</strong> — Total e por período</li>
            <li><strong>Taxa de play</strong> — Percentual de visitors que deram play</li>
            <li><strong>Curva de retenção</strong> — Em que ponto os espectadores saem</li>
            <li><strong>Cliques no CTA</strong> — Quantos clicaram e em que momento</li>
            <li><strong>Dispositivos</strong> — Desktop vs mobile</li>
          </ul>
        `,
      },
      {
        id: 'mapa-de-calor',
        title: 'Mapa de Calor',
        tags: ['heatmap', 'mapa de calor', 'engajamento'],
        updated: 'Abr 2025',
        readTime: 5,
        categoryLabel: 'Analytics e Mapa de Calor',
        popular: true,
        content: `
          <h2>Mapa de Calor</h2>
          <p>O Mapa de Calor é uma representação visual de <strong>onde os espectadores estão mais engajados</strong> no seu vídeo. Ele mostra um gráfico colorido da timeline, indicando quais trechos foram mais assistidos, revistos ou pulados.</p>
          <h3>Como acessar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">No menu lateral, clique em <em>Mapa de Calor</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Selecione o vídeo que deseja analisar</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Analise as zonas quentes (mais assistidas) e frias (mais abandonadas)</div>
            </div>
          </div>
          <h3>Interpretando as cores</h3>
          <div class="doc-table-wrap">
            <table class="doc-table">
              <thead><tr><th>Cor</th><th>Significado</th><th>Ação sugerida</th></tr></thead>
              <tbody>
                <tr><td><strong style="color:#ef4444">Vermelho/Quente</strong></td><td>Trecho mais assistido e revisto</td><td>Este é seu melhor conteúdo — use como referência</td></tr>
                <tr><td><strong style="color:#f59e0b">Amarelo</strong></td><td>Engajamento moderado</td><td>Conteúdo funcional, pode ser otimizado</td></tr>
                <tr><td><strong style="color:#3b82f6">Azul/Frio</strong></td><td>Trecho com pouca visualização</td><td>Revise ou encurte — possível ponto de abandono</td></tr>
              </tbody>
            </table>
          </div>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 Dica avançada:</strong> Se uma zona fria aparece logo antes do CTA, mova o CTA para antes desse trecho. Se a zona quente é no início, seu hook está funcionando bem.
          </div>
        `,
      },
    ],
  },

  /* ═══════════════════════════════════════════════════════════════════════
     4. WEBINÁRIO
  ═══════════════════════════════════════════════════════════════════════ */
  {
    id: 'webinario',
    label: 'Webinário',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2" stroke-linecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>`,
    articles: [
      {
        id: 'simulador-webinar',
        title: 'Simulador de Webinário',
        tags: ['webinário', 'live', 'simulação'],
        updated: 'Abr 2025',
        readTime: 6,
        categoryLabel: 'Webinário',
        popular: true,
        content: `
          <h2>Simulador de Webinário</h2>
          <p>O Simulador de Webinário permite criar <strong>experiências de webinário ao vivo</strong> usando vídeos pré-gravados. Os visitantes veem o vídeo como se fosse uma transmissão ao vivo, com chat interativo simulado.</p>
          <h3>Recursos do Webinário</h3>
          <div class="doc-cards-inline">
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">🎥 Stream de vídeo</div>
              <div>Use vídeos pré-gravados que reproduzem como se fossem ao vivo, com integração YouTube.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">💬 Chat simulado</div>
              <div>Configure mensagens de chat que aparecem automaticamente em horários específicos durante o webinário.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">⚡ Gatilhos de chat</div>
              <div>Defina momentos do vídeo que disparam mensagens, CTAs ou interações no chat.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">📊 Analytics</div>
              <div>Acompanhe quantas pessoas assistiram, engajamento no chat e conversões.</div>
            </div>
          </div>
          <h3>Como criar um webinário</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">No menu lateral, acesse <em>Webinário</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Clique em <em>Novo Webinário</em> e preencha título, descrição e configure o vídeo</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Configure o <em>Chat Simulado</em> — defina mensagens que aparecerão automaticamente durante a transmissão</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">4</div>
              <div class="doc-step-content">Defina os <em>Gatilhos de Chat</em> — momentos do vídeo que disparam CTAs ou interações</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">5</div>
              <div class="doc-step-content">Configure o <em>Stream</em> (origem do vídeo) e publique</div>
            </div>
          </div>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 Dica:</strong> Webinários simulados convertem até 3x mais que VSLs tradicionais porque criam senso de urgência e exclusividade. O chat ativo reforça a prova social.
          </div>
        `,
      },
    ],
  },

  /* ═══════════════════════════════════════════════════════════════════════
     5. CHATBOOSTER
  ═══════════════════════════════════════════════════════════════════════ */
  {
    id: 'chatbooster',
    label: 'ChatBooster',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2" stroke-linecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>`,
    articles: [
      {
        id: 'fluxos-mensagem',
        title: 'Criando fluxos de mensagem',
        tags: ['chat', 'fluxo', 'automação'],
        updated: 'Mar 2025',
        readTime: 7,
        categoryLabel: 'ChatBooster',
        popular: true,
        content: `
          <h2>Fluxos de mensagem</h2>
          <p>Os fluxos de mensagem automatizam a comunicação com seus leads com base no comportamento deles no seu VSL.</p>
          <h3>Tipos de fluxo</h3>
          <div class="doc-cards-inline">
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">⚡ Gatilho por evento</div>
              <div>Dispara quando o lead assiste até determinado % do vídeo ou clica no CTA.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">⏰ Gatilho por tempo</div>
              <div>Dispara N minutos/horas/dias após o lead interagir com o vídeo pela primeira vez.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">🔄 Fluxo sequencial</div>
              <div>Série de mensagens encadeadas com delays entre elas.</div>
            </div>
          </div>
          <h3>Criando seu primeiro fluxo</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">No menu lateral, acesse <em>Chat</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Selecione o chat desejado e clique em <em>Fluxo de Mensagem</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Arraste os blocos de mensagem e configure os delays</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">4</div>
              <div class="doc-step-content">Ative o fluxo e teste com um lead de teste</div>
            </div>
          </div>
        `,
      },
      {
        id: 'design-chat',
        title: 'Design e aparência do chat',
        tags: ['chat', 'design', 'customização'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'ChatBooster',
        content: `
          <h2>Customizando o visual do chat</h2>
          <p>A aparência do chat pode ser completamente personalizada para combinar com o visual do seu site.</p>
          <h3>Como acessar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">No menu lateral, acesse <em>Chat</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Selecione o chat e clique em <em>Design</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Personalize cores, posição, tamanho e comportamento do widget</div>
            </div>
          </div>
          <h3>Elementos customizáveis</h3>
          <ul>
            <li><strong>Cor principal</strong> — Define a cor do header e botão do chat</li>
            <li><strong>Posição</strong> — Canto inferior direito ou esquerdo</li>
            <li><strong>Mensagem de boas-vindas</strong> — Texto exibido ao abrir o chat</li>
            <li><strong>Avatar do atendente</strong> — Foto e nome exibidos no chat</li>
            <li><strong>Ícone do botão</strong> — Personalize o ícone flutuante</li>
          </ul>
        `,
      },
      {
        id: 'integracao-chat',
        title: 'Integração do chat com sistemas',
        tags: ['chat', 'integração', 'CRM'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'ChatBooster',
        content: `
          <h2>Integrando o chat com sistemas externos</h2>
          <p>Cada chat pode ser integrado individualmente com CRMs e ferramentas de automação.</p>
          <h3>Como acessar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">No menu lateral, acesse <em>Chat</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Selecione o chat e clique em <em>Integre Sistema</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Escolha a integração desejada e configure as credenciais</div>
            </div>
          </div>
          <h3>Integrações disponíveis</h3>
          <ul>
            <li><strong>ActiveCampaign</strong> — Envie leads automaticamente para listas e automações</li>
            <li><strong>PipeDrive</strong> — Crie deals automaticamente no seu funil de vendas</li>
            <li><strong>RD Station</strong> — Envie eventos de conversão e crie leads no RD</li>
            <li><strong>Webhooks</strong> — Envie dados para qualquer sistema via HTTP POST</li>
          </ul>
        `,
      },
      {
        id: 'leads',
        title: 'Gerenciando leads',
        tags: ['chat', 'leads', 'CRM'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'ChatBooster',
        content: `
          <h2>Gerenciamento de leads</h2>
          <p>A tela de Leads exibe todos os contatos capturados pelo ChatBooster com seu histórico de interações.</p>
          <h3>Informações disponíveis por lead</h3>
          <ul>
            <li><strong>Nome e e-mail</strong> — capturados via formulário ou integração</li>
            <li><strong>Vídeos assistidos</strong> — quais VSLs e qual % de cada um</li>
            <li><strong>Cliques no CTA</strong> — histórico de intenção de compra</li>
            <li><strong>Última interação</strong> — data/hora da última atividade</li>
            <li><strong>Score</strong> — pontuação automática baseada no engajamento</li>
          </ul>
          <h3>Detalhes do lead</h3>
          <p>Clique em um lead para ver o histórico completo de interações, incluindo todas as mensagens do chat, vídeos assistidos e ações realizadas.</p>
          <h3>Filtrando leads qualificados</h3>
          <p>Use os filtros de <em>Score mínimo</em> e <em>% mínima assistida</em> para encontrar os leads mais prontos para compra.</p>
        `,
      },
    ],
  },

  /* ═══════════════════════════════════════════════════════════════════════
     6. INTEGRAÇÕES
  ═══════════════════════════════════════════════════════════════════════ */
  {
    id: 'integracoes',
    label: 'Integrações',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2" stroke-linecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>`,
    articles: [
      {
        id: 'activecampaign',
        title: 'Integrando com ActiveCampaign',
        tags: ['integração', 'email', 'automação'],
        updated: 'Mar 2025',
        readTime: 5,
        categoryLabel: 'Integrações',
        content: `
          <h2>ActiveCampaign</h2>
          <p>Integre o VSLBooster com o ActiveCampaign para adicionar automaticamente leads que assistiram seu vídeo em listas de automação.</p>
          <h3>Passo a passo</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">
                <strong>Obtenha sua API Key</strong>
                <p>No ActiveCampaign: <em>Configurações → Desenvolvedor → API Key e URL</em></p>
              </div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">
                <strong>Configure no VSLBooster</strong>
                <p>Vá em <em>Integrações → ActiveCampaign</em> e cole a URL da API e a chave.</p>
              </div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">
                <strong>Defina o gatilho</strong>
                <p>Escolha em que momento do vídeo o contato é adicionado à lista (ex: assistiu até 70%).</p>
              </div>
            </div>
          </div>
          <div class="doc-callout doc-callout-warning">
            <strong>⚠️ Permissões:</strong> Certifique-se de que a API Key tem permissão de escrita em contatos e listas.
          </div>
        `,
      },
      {
        id: 'rd-station',
        title: 'Integrando com RD Station',
        tags: ['integração', 'RD Station', 'marketing'],
        updated: 'Fev 2025',
        readTime: 4,
        categoryLabel: 'Integrações',
        content: `
          <h2>RD Station</h2>
          <p>Envie eventos de visualização para o RD Station e dispare automações baseadas no comportamento do lead no seu VSL.</p>
          <h3>Configuração</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse <em>Integrações → RD Station</em> no VSLBooster</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Clique em "Conectar com RD Station" e autorize via OAuth</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Mapeie os eventos (play, CTA clicado, vídeo completo) para funis do RD</div>
            </div>
          </div>
        `,
      },
      {
        id: 'pipedrive',
        title: 'Integrando com PipeDrive',
        tags: ['integração', 'PipeDrive', 'CRM', 'vendas'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'Integrações',
        content: `
          <h2>PipeDrive</h2>
          <p>Integre o VSLBooster com o PipeDrive para criar automaticamente <strong>deals no seu funil de vendas</strong> quando leads interagem com seus vídeos ou chat.</p>
          <h3>Configuração</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">
                <strong>Obtenha seu API Token</strong>
                <p>No PipeDrive: <em>Configurações → Dados pessoais → API</em></p>
              </div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">
                <strong>Configure no VSLBooster</strong>
                <p>Acesse <em>Integrações → PipeDrive</em> e cole o token e o domínio da sua conta.</p>
              </div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">
                <strong>Mapeie o funil</strong>
                <p>Escolha em qual estágio do pipeline os novos deals serão criados.</p>
              </div>
            </div>
          </div>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 Dica:</strong> Configure o gatilho para criar o deal quando o lead clicar no CTA — assim só chegam leads com real intenção de compra.
          </div>
        `,
      },
      {
        id: 'webhooks',
        title: 'Webhooks — Integrações customizadas',
        tags: ['integração', 'webhook', 'API'],
        updated: 'Mar 2025',
        readTime: 6,
        categoryLabel: 'Integrações',
        popular: true,
        content: `
          <h2>Webhooks</h2>
          <p>Webhooks permitem enviar dados de eventos do VSLBooster para qualquer sistema externo em tempo real.</p>
          <h3>Eventos disponíveis</h3>
          <div class="doc-table-wrap">
            <table class="doc-table">
              <thead><tr><th>Evento</th><th>Quando dispara</th></tr></thead>
              <tbody>
                <tr><td><code>video.play</code></td><td>Usuário deu play no vídeo</td></tr>
                <tr><td><code>video.progress</code></td><td>Usuário atingiu marco de % (25%, 50%, 75%, 100%)</td></tr>
                <tr><td><code>video.cta_click</code></td><td>Usuário clicou no botão CTA</td></tr>
                <tr><td><code>video.complete</code></td><td>Usuário assistiu 100% do vídeo</td></tr>
              </tbody>
            </table>
          </div>
          <h3>Payload enviado</h3>
          <div class="doc-code-block">
<pre>{
  "event": "video.cta_click",
  "videoId": "abc123",
  "timestamp": "2025-03-01T14:22:00Z",
  "viewer": {
    "email": "usuario@email.com",
    "ip": "189.x.x.x",
    "country": "BR"
  },
  "progress": 82
}</pre>
          </div>
          <h3>Configuração de webhooks</h3>
          <p>Os webhooks podem ser configurados de duas formas:</p>
          <ul>
            <li><strong>Por chat/vídeo:</strong> Na tela de integração de cada chat ou vídeo</li>
            <li><strong>Global (Admin):</strong> Em <em>Administração → Webhooks</em> para configurar webhooks que disparam para todos os eventos da conta</li>
          </ul>
        `,
      },
    ],
  },

  /* ═══════════════════════════════════════════════════════════════════════
     7. TESTES A/B E SEGURANÇA
  ═══════════════════════════════════════════════════════════════════════ */
  {
    id: 'testes-seguranca',
    label: 'Testes A/B e Segurança',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
    articles: [
      {
        id: 'testes-ab',
        title: 'Testes A/B de vídeos',
        tags: ['teste AB', 'conversão', 'otimização'],
        updated: 'Abr 2025',
        readTime: 5,
        categoryLabel: 'Testes A/B e Segurança',
        content: `
          <h2>Testes A/B</h2>
          <p>Os Testes A/B permitem comparar <strong>duas versões do mesmo VSL</strong> para descobrir qual converte melhor. O sistema divide automaticamente o tráfego entre as versões e mostra os resultados em tempo real.</p>
          <h3>Como criar um teste A/B</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse <em>Testes A/B</em> no menu lateral</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Clique em <em>Novo Teste</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Selecione o <strong>vídeo original (A)</strong> e o <strong>vídeo variante (B)</strong></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">4</div>
              <div class="doc-step-content">Defina a <strong>divisão de tráfego</strong> (50/50 é o recomendado)</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">5</div>
              <div class="doc-step-content">Ative o teste e aguarde dados suficientes (mínimo 100 visualizações por versão)</div>
            </div>
          </div>
          <h3>O que testar</h3>
          <ul>
            <li><strong>Hook/Abertura</strong> — Os primeiros 30 segundos são cruciais</li>
            <li><strong>Thumbnail</strong> — Teste diferentes imagens de capa</li>
            <li><strong>Momento do CTA</strong> — Experimente tempos diferentes para o botão aparecer</li>
            <li><strong>Duração do vídeo</strong> — Versão mais curta vs mais longa</li>
          </ul>
          <div class="doc-callout doc-callout-warning">
            <strong>⚠️ Importante:</strong> Aguarde pelo menos 100 visualizações em cada versão antes de tomar uma decisão. Resultados com poucas visualizações não são estatisticamente confiáveis.
          </div>
        `,
      },
      {
        id: 'seguranca-video',
        title: 'Segurança e proteção do vídeo',
        tags: ['segurança', 'proteção', 'DRM'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'Testes A/B e Segurança',
        content: `
          <h2>Segurança do vídeo</h2>
          <p>O VSLBooster oferece recursos de segurança para proteger seus vídeos contra cópias e acessos não autorizados.</p>
          <h3>Recursos de proteção</h3>
          <ul>
            <li><strong>Domínios permitidos</strong> — Restrinja em quais sites o player pode ser embutido. O vídeo só reproduz nos domínios que você autorizar.</li>
            <li><strong>Bloqueio de download</strong> — Impede o download direto do arquivo de vídeo pelo navegador</li>
            <li><strong>Bloqueio de inspeção</strong> — Dificulta o acesso ao código-fonte e DevTools na página do player</li>
            <li><strong>Marca d'água</strong> — Adicione watermark dinâmico com dados do visitante para rastrear vazamentos</li>
          </ul>
          <h3>Como configurar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse <em>Segurança</em> no menu lateral</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Selecione o vídeo e configure as opções de proteção</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Adicione os domínios autorizados para embed</div>
            </div>
          </div>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 Recomendação:</strong> Sempre configure pelo menos a lista de domínios permitidos. Isso impede que concorrentes copiem seu embed e exibam seu VSL em outras páginas.
          </div>
        `,
      },
    ],
  },

  /* ═══════════════════════════════════════════════════════════════════════
     8. ADMINISTRAÇÃO
  ═══════════════════════════════════════════════════════════════════════ */
  {
    id: 'administracao',
    label: 'Administração',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>`,
    articles: [
      {
        id: 'admin-overview',
        title: 'Visão Geral (Admin)',
        tags: ['admin', 'overview', 'painel'],
        updated: 'Abr 2025',
        readTime: 3,
        categoryLabel: 'Administração',
        content: `
          <h2>Painel de Administração</h2>
          <p>O painel administrativo oferece uma visão geral da plataforma com métricas de uso, usuários ativos e recursos consumidos.</p>
          <h3>Como acessar</h3>
          <p>No menu lateral, expanda <em>Administração</em> e clique em <em>Visão Geral</em>.</p>
          <div class="doc-callout doc-callout-info">
            <strong>ℹ️ Permissão:</strong> Apenas usuários com perfil <strong>Admin</strong> podem acessar o painel de administração.
          </div>
          <h3>Métricas exibidas</h3>
          <ul>
            <li><strong>Total de usuários</strong> — Quantidade de contas ativas</li>
            <li><strong>Vídeos hospedados</strong> — Total de vídeos na plataforma</li>
            <li><strong>Chats ativos</strong> — Fluxos de chat em operação</li>
            <li><strong>Uso de storage</strong> — Espaço utilizado no S3</li>
          </ul>
        `,
      },
      {
        id: 'admin-usuarios',
        title: 'Gerenciando usuários',
        tags: ['admin', 'usuários', 'contas'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'Administração',
        content: `
          <h2>Gerenciamento de usuários</h2>
          <p>A tela de usuários permite visualizar, editar e gerenciar todas as contas da plataforma.</p>
          <h3>Como acessar</h3>
          <p>Em <em>Administração → Usuários</em>.</p>
          <h3>Funcionalidades</h3>
          <ul>
            <li><strong>Listar usuários</strong> — Veja todos os usuários com filtros por status, plano e data de cadastro</li>
            <li><strong>Ver detalhes</strong> — Clique em um usuário para ver perfil completo, vídeos, chats e métricas</li>
            <li><strong>Alterar status</strong> — Ative ou desative contas</li>
            <li><strong>Alterar plano</strong> — Mude o plano de um usuário</li>
            <li><strong>Login como usuário</strong> — Acesse a conta de um usuário para suporte (admin only)</li>
          </ul>
          <div class="doc-callout doc-callout-warning">
            <strong>⚠️ Cuidado:</strong> A funcionalidade "Login como usuário" deve ser usada apenas para fins de suporte técnico. Todas as ações são registradas.
          </div>
        `,
      },
      {
        id: 'admin-permissoes',
        title: 'Permissões e controle de acesso',
        tags: ['admin', 'permissões', 'ACL', 'roles'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'Administração',
        content: `
          <h2>Sistema de permissões</h2>
          <p>O VSLBooster utiliza um sistema de controle de acesso baseado em roles (RBAC) para definir o que cada tipo de usuário pode acessar.</p>
          <h3>Roles disponíveis</h3>
          <div class="doc-table-wrap">
            <table class="doc-table">
              <thead><tr><th>Role</th><th>Descrição</th><th>Acesso</th></tr></thead>
              <tbody>
                <tr><td><strong>Admin</strong></td><td>Administrador da plataforma</td><td>Acesso total a todas as funcionalidades</td></tr>
                <tr><td><strong>Usuário</strong></td><td>Usuário padrão</td><td>Seus vídeos, chats, leads e analytics</td></tr>
              </tbody>
            </table>
          </div>
          <h3>Como configurar</h3>
          <p>Acesse <em>Administração → Permissões</em> para gerenciar as roles e definir quais recursos cada perfil pode acessar.</p>
        `,
      },
      {
        id: 'admin-email',
        title: 'Configuração de e-mail (Resend)',
        tags: ['admin', 'email', 'resend', 'SMTP'],
        updated: 'Abr 2025',
        readTime: 3,
        categoryLabel: 'Administração',
        content: `
          <h2>Configuração de e-mail</h2>
          <p>O VSLBooster usa o <strong>Resend</strong> como provedor de e-mail transacional para envio de notificações, verificação de conta e redefinição de senha.</p>
          <h3>Como configurar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse <em>Administração → Email (Resend)</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Insira sua <strong>API Key do Resend</strong></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Configure o <strong>e-mail remetente</strong> e o <strong>nome exibido</strong></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">4</div>
              <div class="doc-step-content">Envie um e-mail de teste para verificar</div>
            </div>
          </div>
          <div class="doc-callout doc-callout-info">
            <strong>ℹ️ Pré-requisito:</strong> Você precisa ter um domínio verificado no Resend para enviar e-mails. Acesse <a href="https://resend.com" target="_blank">resend.com</a> para configurar.
          </div>
        `,
      },
      {
        id: 'admin-aws-scripts',
        title: 'AWS S3 e Scripts do Site',
        tags: ['admin', 'AWS', 'S3', 'storage', 'scripts'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'Administração',
        content: `
          <h2>Configuração AWS S3</h2>
          <p>O VSLBooster armazena vídeos e assets no <strong>Amazon S3</strong>. Configure suas credenciais AWS para habilitar o upload de vídeos.</p>
          <h3>Como configurar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse <em>Administração → AWS S3</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Insira o <strong>Access Key</strong>, <strong>Secret Key</strong>, <strong>Bucket</strong> e <strong>Region</strong></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Teste a conexão para verificar se as credenciais estão corretas</div>
            </div>
          </div>

          <h2>Scripts do Site</h2>
          <p>Em <em>Administração → Scripts do Site</em> você pode gerenciar scripts personalizados que são injetados nos sites dos clientes. Útil para rastreamento, analytics e pixels de conversão.</p>
          <h3>Funcionalidades</h3>
          <ul>
            <li><strong>Criar scripts</strong> — Adicione código JavaScript/HTML para ser injetado</li>
            <li><strong>Ativar/desativar</strong> — Controle quais scripts estão ativos</li>
            <li><strong>Escopo</strong> — Defina se o script é global ou específico por domínio</li>
          </ul>
          <div class="doc-callout doc-callout-warning">
            <strong>⚠️ Cuidado:</strong> Scripts injetados rodam nos sites dos clientes. Sempre teste em ambiente de desenvolvimento antes de ativar em produção.
          </div>
        `,
      },
      {
        id: 'admin-webhooks',
        title: 'Webhooks (Admin)',
        tags: ['admin', 'webhooks', 'automação'],
        updated: 'Abr 2025',
        readTime: 3,
        categoryLabel: 'Administração',
        content: `
          <h2>Webhooks globais</h2>
          <p>Em <em>Administração → Webhooks</em> você configura webhooks que disparam para <strong>todos os eventos da plataforma</strong>, independente do vídeo ou chat específico.</p>
          <h3>Diferença entre webhooks de vídeo e webhooks admin</h3>
          <div class="doc-table-wrap">
            <table class="doc-table">
              <thead><tr><th>Tipo</th><th>Escopo</th><th>Onde configurar</th></tr></thead>
              <tbody>
                <tr><td><strong>Webhook por vídeo/chat</strong></td><td>Dispara apenas para eventos daquele vídeo ou chat</td><td>Integração do vídeo/chat</td></tr>
                <tr><td><strong>Webhook global (admin)</strong></td><td>Dispara para todos os eventos da conta</td><td>Administração → Webhooks</td></tr>
              </tbody>
            </table>
          </div>
          <h3>Como configurar</h3>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse <em>Administração → Webhooks</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Clique em <em>Novo Webhook</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Insira a <strong>URL de destino</strong> e selecione os <strong>eventos</strong> desejados</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">4</div>
              <div class="doc-step-content">Ative e teste com um envio manual</div>
            </div>
          </div>
        `,
      },
      {
        id: 'admin-chat-suporte',
        title: 'Chat de Suporte',
        tags: ['admin', 'suporte', 'atendimento'],
        updated: 'Abr 2025',
        readTime: 3,
        categoryLabel: 'Administração',
        content: `
          <h2>Configuração do Chat de Suporte</h2>
          <p>Configure o chat de suporte interno da plataforma para atender seus usuários diretamente.</p>
          <h3>Como acessar</h3>
          <p>Em <em>Administração → Chat de Suporte</em>.</p>
          <h3>Funcionalidades</h3>
          <ul>
            <li><strong>Ativar/desativar</strong> — Controle se o chat de suporte está disponível</li>
            <li><strong>Horário de atendimento</strong> — Defina os horários em que o suporte está online</li>
            <li><strong>Mensagem de boas-vindas</strong> — Configure a saudação inicial</li>
            <li><strong>Mensagem fora do horário</strong> — Texto exibido quando o suporte não está disponível</li>
          </ul>
        `,
      },
    ],
  },

  /* ═══════════════════════════════════════════════════════════════════════
     9. CONTA E PLANOS
  ═══════════════════════════════════════════════════════════════════════ */
  {
    id: 'conta',
    label: 'Conta e Planos',
    icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`,
    articles: [
      {
        id: 'gerenciando-perfil',
        title: 'Gerenciando seu perfil',
        tags: ['conta', 'perfil', 'configurações'],
        updated: 'Mar 2025',
        readTime: 3,
        categoryLabel: 'Conta e Planos',
        content: `
          <h2>Perfil do usuário</h2>
          <p>Acesse seu perfil pelo ícone do usuário no canto superior direito ou em <em>Menu → Perfil</em>.</p>
          <h3>Abas disponíveis</h3>
          <div class="doc-table-wrap">
            <table class="doc-table">
              <thead><tr><th>Aba</th><th>O que você pode fazer</th></tr></thead>
              <tbody>
                <tr><td><strong>Geral</strong></td><td>Alterar nome, foto de perfil e dados básicos</td></tr>
                <tr><td><strong>Senha</strong></td><td>Trocar sua senha com validação de força</td></tr>
                <tr><td><strong>Notificações</strong></td><td>Configurar alertas de analytics e leads</td></tr>
                <tr><td><strong>Endereços</strong></td><td>Gerenciar endereços de cobrança</td></tr>
                <tr><td><strong>Plano</strong></td><td>Ver seu plano atual e opções de upgrade</td></tr>
              </tbody>
            </table>
          </div>
        `,
      },
      {
        id: 'planos',
        title: 'Planos e assinaturas',
        tags: ['plano', 'assinatura', 'cobrança'],
        updated: 'Mar 2025',
        readTime: 4,
        categoryLabel: 'Conta e Planos',
        popular: true,
        content: `
          <h2>Planos VSLBooster</h2>
          <p>O VSLBooster oferece dois planos principais:</p>
          <div class="doc-cards-inline">
            <div class="doc-card-inline">
              <div class="doc-card-inline-title" style="color:#0D3349">🚀 Starter</div>
              <div>Ideal para quem está começando. Inclui até 5 vídeos, analytics básico e 1 integração.</div>
            </div>
            <div class="doc-card-inline" style="border-color:#E86A10">
              <div class="doc-card-inline-title" style="color:#E86A10">⚡ Pro</div>
              <div>Vídeos ilimitados, analytics avançado, todas as integrações, ChatBooster e suporte prioritário.</div>
            </div>
          </div>
          <h3>Alterando seu plano</h3>
          <p>Acesse <em>Perfil → Plano</em> para ver os detalhes do seu plano atual e fazer upgrade ou downgrade.</p>
          <div class="doc-callout doc-callout-tip">
            <strong>💡 Dica:</strong> O upgrade é imediato e você paga apenas a diferença proporcional ao período restante.
          </div>
        `,
      },
      {
        id: 'pagamentos',
        title: 'Pagamentos e cobrança',
        tags: ['pagamento', 'cobrança', 'Pix', 'boleto'],
        updated: 'Abr 2025',
        readTime: 4,
        categoryLabel: 'Conta e Planos',
        content: `
          <h2>Pagamentos</h2>
          <p>Gerencie seus pagamentos, histórico de cobranças e métodos de pagamento.</p>
          <h3>Como acessar</h3>
          <p>Em <em>Administração → Pagamentos</em> (admin) ou <em>Perfil → Plano</em> (usuário).</p>
          <h3>Métodos de pagamento</h3>
          <div class="doc-cards-inline">
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">💳 Cartão de crédito</div>
              <div>Pagamento recorrente com renovação automática. Aceita Visa, Mastercard, Elo e Amex.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">📱 Pix</div>
              <div>Pagamento instantâneo via QR Code. Confirmação em poucos segundos.</div>
            </div>
            <div class="doc-card-inline">
              <div class="doc-card-inline-title">📄 Boleto</div>
              <div>Boleto bancário com prazo de 3 dias úteis para compensação.</div>
            </div>
          </div>
          <h3>Histórico</h3>
          <p>Na tela de pagamentos, veja o histórico completo com status de cada cobrança (pago, pendente, vencido).</p>
        `,
      },
      {
        id: 'alterando-senha',
        title: 'Alterando sua senha',
        tags: ['conta', 'senha', 'segurança'],
        updated: 'Jan 2025',
        readTime: 2,
        categoryLabel: 'Conta e Planos',
        content: `
          <h2>Como alterar sua senha</h2>
          <div class="doc-steps">
            <div class="doc-step">
              <div class="doc-step-num">1</div>
              <div class="doc-step-content">Acesse <em>Perfil → Senha</em></div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">2</div>
              <div class="doc-step-content">Digite sua senha atual para confirmar identidade</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">3</div>
              <div class="doc-step-content">Digite a nova senha — observe a barra de força mínima (verde)</div>
            </div>
            <div class="doc-step">
              <div class="doc-step-num">4</div>
              <div class="doc-step-content">Confirme a nova senha e clique em Salvar</div>
            </div>
          </div>
          <h3>Requisitos de senha forte</h3>
          <ul>
            <li>Mínimo 8 caracteres</li>
            <li>Pelo menos 1 letra maiúscula</li>
            <li>Pelo menos 1 número</li>
            <li>Pelo menos 1 caractere especial (!@#$...)</li>
          </ul>
          <h3>Esqueceu sua senha?</h3>
          <p>Na tela de login, clique em <em>"Esqueceu sua senha?"</em> para receber um link de redefinição por e-mail.</p>
        `,
      },
      {
        id: 'notificacoes',
        title: 'Notificações',
        tags: ['conta', 'notificações', 'alertas'],
        updated: 'Abr 2025',
        readTime: 2,
        categoryLabel: 'Conta e Planos',
        content: `
          <h2>Central de Notificações</h2>
          <p>O VSLBooster envia notificações em tempo real sobre eventos importantes da sua conta.</p>
          <h3>Como acessar</h3>
          <p>Clique no ícone de sino no canto superior direito da barra de navegação. Ou acesse <em>Notificações</em> para ver o histórico completo.</p>
          <h3>Tipos de notificação</h3>
          <ul>
            <li><strong>Novos leads</strong> — Quando um novo lead é capturado pelo chat</li>
            <li><strong>Marcos de visualização</strong> — Quando um vídeo atinge marcos de views</li>
            <li><strong>Atualizações da plataforma</strong> — Novas funcionalidades e melhorias</li>
            <li><strong>Alertas de uso</strong> — Quando se aproxima do limite do plano</li>
          </ul>
          <h3>Configurando notificações</h3>
          <p>Em <em>Perfil → Notificações</em>, escolha quais tipos de notificação deseja receber e por qual canal (plataforma, e-mail ou ambos).</p>
        `,
      },
    ],
  },
]

// Achata todos os artigos com referência à categoria
function flatArticles(cats) {
  return cats.flatMap(cat =>
    cat.articles.map(art => ({ ...art, categoryLabel: cat.label, _cat: cat }))
  )
}

export default {
  name: 'DocumentacaoIndex',

  data() {
    const openCats = {}
    CATEGORIES.forEach(c => { openCats[c.id] = false })
    openCats[CATEGORIES[0].id] = true // abre a primeira por padrão

    return {
      categories:    CATEGORIES,
      openCats,
      activeArticle: null,
      activeCategory: null,
      search:        '',
      searchResults: [],
      feedback:      null,
      sidebarOpen:   false,
    }
  },

  computed: {
    allArticles() {
      return flatArticles(this.categories)
    },

    popularArticles() {
      return this.allArticles.filter(a => a.popular).slice(0, 6)
    },

    activeArticleIndex() {
      if (!this.activeArticle || !this.activeCategory) return -1
      return this.activeCategory.articles.findIndex(a => a.id === this.activeArticle.id)
    },

    prevArticle() {
      if (!this.activeCategory) return null
      const idx = this.activeArticleIndex
      return idx > 0 ? { ...this.activeCategory.articles[idx - 1], _cat: this.activeCategory, categoryLabel: this.activeCategory.label } : null
    },

    nextArticle() {
      if (!this.activeCategory) return null
      const idx = this.activeArticleIndex
      const arts = this.activeCategory.articles
      return idx < arts.length - 1 ? { ...arts[idx + 1], _cat: this.activeCategory, categoryLabel: this.activeCategory.label } : null
    },
  },

  methods: {
    toggleCat(catId, openArticle) {
      this.openCats[catId] = !this.openCats[catId]
      if (openArticle && this.openCats[catId]) {
        const cat = this.categories.find(c => c.id === catId)
        if (cat && cat.articles.length) this.openArticle(cat.articles[0], cat)
      }
    },

    openArticle(art, cat) {
      this.activeCategory = cat || art._cat || this.categories.find(c => c.articles.some(a => a.id === art.id))
      this.activeArticle  = art
      this.feedback       = null
      this.search         = ''
      this.searchResults  = []
      this.sidebarOpen    = false
      if (this.activeCategory) this.openCats[this.activeCategory.id] = true
      this.$nextTick(() => window.scrollTo(0, 0))
    },

    goHome() {
      this.activeArticle  = null
      this.activeCategory = null
      this.feedback       = null
    },

    onSearch() {
      const q = this.search.trim().toLowerCase()
      if (!q) { this.searchResults = []; return }
      this.searchResults = this.allArticles.filter(a =>
        a.title.toLowerCase().includes(q) ||
        (a.tags || []).some(t => t.toLowerCase().includes(q)) ||
        (a.content || '').toLowerCase().includes(q)
      ).slice(0, 8)
    },

    clearSearch() {
      this.search = ''
      this.searchResults = []
    },
  },
}
</script>

<style scoped>
/* ── Layout ───────────────────────────────────────────────────────────────── */
.doc-layout {
  display: flex;
  min-height: calc(100vh - 4rem);
  background: var(--dk-bg-page, #f8f9fb);
  font-family: Inter, system-ui, sans-serif;
  position: relative;
}

/* ── Sidebar ───────────────────────────────────────────────────────────────── */
.doc-sidebar {
  width: 270px;
  min-width: 270px;
  background: var(--dk-bg-card, #fff);
  border-right: 1px solid var(--dk-border, #eaecf0);
  display: flex;
  flex-direction: column;
  height: 100%;
  position: sticky;
  top: 0;
  overflow-y: auto;
}
.doc-sidebar::-webkit-scrollbar { width: 4px; }
.doc-sidebar::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 4px; }

.doc-sidebar-header {
  padding: 20px 18px 14px;
  border-bottom: 1px solid var(--dk-border, #eaecf0);
  position: sticky;
  top: 0;
  background: var(--dk-bg-card, #fff);
  z-index: 2;
}

.doc-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 15px;
  color: var(--dk-text-primary, #0D3349);
  margin-bottom: 14px;
}

/* Busca */
.doc-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.doc-search-icon {
  position: absolute;
  left: 10px;
  color: var(--dk-text-muted, #9ca3af);
  pointer-events: none;
}
.doc-search-input {
  width: 100%;
  padding: 7px 30px 7px 32px;
  border: 1.5px solid var(--dk-border, #e4e4e7);
  border-radius: 8px;
  font-size: 13px;
  background: var(--dk-bg-page, #f8f9fb);
  outline: none;
  transition: border-color .15s;
  font-family: inherit;
}
.doc-search-input:focus { border-color: #E86A10; background: var(--dk-bg-card, #fff); }
.doc-search-clear {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  font-size: 16px;
  color: var(--dk-text-muted, #9ca3af);
  cursor: pointer;
  line-height: 1;
}

/* Resultados da busca */
.doc-search-results {
  padding: 8px;
}
.doc-no-results {
  text-align: center;
  padding: 20px;
  color: var(--dk-text-muted, #9ca3af);
  font-size: 13px;
}
.doc-search-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  transition: background .1s;
}
.doc-search-item:hover { background: var(--dk-bg-inset, #f5f5f5); }
.doc-search-item-cat {
  font-size: 10px;
  color: #E86A10;
  text-transform: uppercase;
  letter-spacing: .5px;
  font-weight: 600;
}
.doc-search-item-title {
  font-size: 13px;
  color: var(--dk-text-primary, #0D3349);
  font-weight: 500;
}

/* Navegação lateral */
.doc-nav { padding: 10px 8px 20px; }
.doc-nav-category { margin-bottom: 2px; }

.doc-nav-cat-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: var(--dk-text-primary, #374151);
  transition: background .1s;
}
.doc-nav-cat-btn:hover { background: var(--dk-bg-inset, #f5f5f5); }
.doc-cat-active { color: var(--dk-text-primary, #0D3349); }
.doc-cat-icon { display: flex; align-items: center; flex-shrink: 0; }
.doc-cat-label { flex: 1; }
.doc-cat-chevron { color: var(--dk-text-muted, #9ca3af); transition: transform .2s; flex-shrink: 0; }
.doc-cat-chevron.rotated { transform: rotate(180deg); }

.doc-nav-articles { padding: 2px 0 4px 36px; }
.doc-nav-article {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 12.5px;
  color: var(--dk-text-secondary, #6b7280);
  transition: background .1s, color .1s;
  border-left: 2px solid transparent;
}
.doc-nav-article:hover { background: var(--dk-bg-inset, #f5f5f5); color: var(--dk-text-primary, #0D3349); }
.doc-art-active {
  background: #fff5ee !important;
  color: #E86A10 !important;
  border-left-color: #E86A10 !important;
  font-weight: 600;
}

/* ── Main ───────────────────────────────────────────────────────────────── */
.doc-main {
  flex: 1;
  min-width: 0;
  padding: 0 0 40px;
}

.doc-mobile-header {
  display: none;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--dk-border, #eaecf0);
  background: var(--dk-bg-card, #fff);
  position: sticky;
  top: 0;
  z-index: 10;
}
.doc-menu-btn {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: var(--dk-text-primary, #374151);
}
.doc-mobile-title {
  font-weight: 700;
  font-size: 15px;
  color: var(--dk-text-primary, #0D3349);
}

/* ── Home ──────────────────────────────────────────────────────────────── */
.doc-home { padding: 0 40px 40px; }

.doc-home-hero {
  background: linear-gradient(135deg, #0D3349 0%, #1a4f6e 100%);
  border-radius: 16px;
  padding: 40px;
  margin: 28px 0 28px;
  color: #fff;
}
.doc-home-title {
  font-size: 26px;
  font-weight: 800;
  margin: 0 0 8px;
  color: #fff;
}
.doc-home-title span { color: #F5B935; }
.doc-home-sub {
  font-size: 14px;
  opacity: .85;
  margin: 0 0 20px;
  max-width: 500px;
}
.doc-home-search-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255,255,255,.12);
  border: 1.5px solid rgba(255,255,255,.25);
  border-radius: 10px;
  padding: 10px 16px;
  max-width: 420px;
}
.doc-home-search {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font-size: 14px;
  color: #fff;
  font-family: inherit;
}
.doc-home-search::placeholder { color: rgba(255,255,255,.6); }

/* Grid de categorias */
.doc-cat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
  margin-bottom: 32px;
}
.doc-cat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--dk-bg-card, #fff);
  border: 1.5px solid var(--dk-border, #eaecf0);
  border-radius: 12px;
  padding: 16px 18px;
  cursor: pointer;
  text-align: left;
  transition: border-color .15s, box-shadow .15s, transform .1s;
}
.doc-cat-card:hover {
  border-color: #E86A10;
  box-shadow: 0 4px 16px rgba(232,106,16,.1);
  transform: translateY(-1px);
}
.doc-cat-card-icon { display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; background: #fff5ee; border-radius: 10px; flex-shrink: 0; }
.doc-cat-card-info { flex: 1; }
.doc-cat-card-label { font-weight: 700; font-size: 14px; color: var(--dk-text-primary, #0D3349); }
.doc-cat-card-count { font-size: 12px; color: var(--dk-text-muted, #9ca3af); margin-top: 2px; }

/* Artigos populares */
.doc-section-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--dk-text-primary, #0D3349);
  margin: 0 0 14px;
}
.doc-popular-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
}
.doc-popular-card {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--dk-bg-card, #fff);
  border: 1.5px solid var(--dk-border, #eaecf0);
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  text-align: left;
  transition: border-color .15s, box-shadow .15s;
}
.doc-popular-card:hover {
  border-color: #E86A10;
  box-shadow: 0 4px 12px rgba(232,106,16,.08);
}
.doc-popular-tag {
  font-size: 10px;
  color: #E86A10;
  text-transform: uppercase;
  letter-spacing: .5px;
  font-weight: 600;
  flex-shrink: 0;
}
.doc-popular-title {
  flex: 1;
  font-size: 13px;
  color: var(--dk-text-primary, #374151);
  font-weight: 500;
  line-height: 1.4;
}

/* ── Artigo ─────────────────────────────────────────────────────────────── */
.doc-article { max-width: 760px; margin: 0 auto; padding: 28px 40px 40px; }

/* Breadcrumb */
.doc-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--dk-text-muted, #9ca3af);
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.doc-bc-home {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #E86A10;
  font-size: 12.5px;
  padding: 0;
  font-family: inherit;
}
.doc-bc-home:hover { text-decoration: underline; }
.doc-bc-cat { color: var(--dk-text-muted, #9ca3af); }
.doc-bc-art { color: var(--dk-text-primary, #374151); font-weight: 500; }

/* Header do artigo */
.doc-art-header { margin-bottom: 28px; }
.doc-art-tags { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
.doc-tag {
  background: #fff5ee;
  color: #E86A10;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
}
.doc-art-title {
  font-size: 26px;
  font-weight: 800;
  color: var(--dk-text-primary, #0D3349);
  margin: 0 0 10px;
  line-height: 1.3;
}
.doc-art-meta {
  font-size: 12.5px;
  color: var(--dk-text-muted, #9ca3af);
  display: flex;
  align-items: center;
  gap: 8px;
}
.doc-art-dot { opacity: .5; }

/* Corpo do artigo */
.doc-art-body { color: var(--dk-text-primary, #374151); line-height: 1.75; font-size: 14.5px; }
.doc-art-body >>> h2 { font-size: 19px; font-weight: 700; color: var(--dk-text-primary, #0D3349); margin: 28px 0 12px; border-bottom: 1px solid #eaecf0; padding-bottom: 8px; }
.doc-art-body >>> h3 { font-size: 15px; font-weight: 700; color: var(--dk-text-primary, #374151); margin: 20px 0 8px; }
.doc-art-body >>> p { margin: 0 0 14px; }
.doc-art-body >>> ul, .doc-art-body >>> ol { padding-left: 20px; margin: 0 0 14px; }
.doc-art-body >>> li { margin-bottom: 6px; }
.doc-art-body >>> strong { color: var(--dk-text-primary, #0D3349); }
.doc-art-body >>> code { background: var(--dk-bg-inset, #f3f4f6); border-radius: 4px; padding: 1px 6px; font-size: 13px; color: #E86A10; font-family: monospace; }
.doc-art-body >>> em { color: #E86A10; font-style: normal; font-weight: 500; }

/* Callouts */
.doc-art-body >>> .doc-callout {
  border-radius: 10px;
  padding: 14px 16px;
  margin: 18px 0;
  font-size: 13.5px;
  border-left: 4px solid;
}
.doc-art-body >>> .doc-callout-tip { background: #fff5ee; border-color: #E86A10; color: #7c4a0f; }
.doc-art-body >>> .doc-callout-warning { background: #fffbeb; border-color: #f59e0b; color: #7c5f0f; }
.doc-art-body >>> .doc-callout-info { background: #eff6ff; border-color: #3b82f6; color: #1e40af; }

/* Steps */
.doc-art-body >>> .doc-steps { display: flex; flex-direction: column; gap: 12px; margin: 16px 0; }
.doc-art-body >>> .doc-step {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: var(--dk-bg-page, #f8f9fb);
  border-radius: 10px;
  padding: 14px 16px;
}
.doc-art-body >>> .doc-step-num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #0D3349;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.doc-art-body >>> .doc-step-content { flex: 1; }
.doc-art-body >>> .doc-step-content p { margin: 4px 0 0; font-size: 13px; color: var(--dk-text-secondary, #6b7280); }

/* Cards inline */
.doc-art-body >>> .doc-cards-inline { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; margin: 16px 0; }
.doc-art-body >>> .doc-card-inline {
  background: var(--dk-bg-card, #fff);
  border: 1.5px solid var(--dk-border, #eaecf0);
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 13px;
  color: var(--dk-text-secondary, #6b7280);
}
.doc-art-body >>> .doc-card-inline-title { font-weight: 700; font-size: 14px; color: var(--dk-text-primary, #0D3349); margin-bottom: 6px; }

/* Tabela */
.doc-art-body >>> .doc-table-wrap { overflow-x: auto; margin: 16px 0; }
.doc-art-body >>> .doc-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.doc-art-body >>> .doc-table th { background: #0D3349; color: #fff; padding: 10px 14px; text-align: left; font-weight: 600; }
.doc-art-body >>> .doc-table td { padding: 10px 14px; border-bottom: 1px solid #eaecf0; color: var(--dk-text-primary, #374151); }
.doc-art-body >>> .doc-table tr:last-child td { border-bottom: none; }
.doc-art-body >>> .doc-table tr:nth-child(even) td { background: var(--dk-bg-page, #f8f9fb); }

/* Code block */
.doc-art-body >>> .doc-code-block {
  background: #1e2433;
  border-radius: 10px;
  padding: 16px 18px;
  margin: 16px 0;
  overflow-x: auto;
}
.doc-art-body >>> .doc-code-block pre {
  margin: 0;
  font-size: 13px;
  color: #a8d8ea;
  font-family: 'Fira Code', 'Courier New', monospace;
  white-space: pre;
  line-height: 1.6;
}

/* Navegação prev/next */
.doc-art-nav {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin: 40px 0 24px;
  padding-top: 24px;
  border-top: 1px solid var(--dk-border, #eaecf0);
}
.doc-nav-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--dk-bg-card, #fff);
  border: 1.5px solid var(--dk-border, #eaecf0);
  border-radius: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
  text-align: left;
  max-width: 48%;
}
.doc-nav-btn:hover { border-color: #E86A10; box-shadow: 0 4px 12px rgba(232,106,16,.1); }
.doc-nav-next { flex-direction: row-reverse; text-align: right; }
.doc-nav-btn-label { font-size: 11px; color: var(--dk-text-muted, #9ca3af); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 2px; }
.doc-nav-btn-title { font-size: 13px; font-weight: 600; color: var(--dk-text-primary, #0D3349); }

/* Feedback */
.doc-feedback {
  background: var(--dk-bg-card, #fff);
  border: 1.5px solid var(--dk-border, #eaecf0);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
}
.doc-feedback-title { font-size: 14px; font-weight: 600; color: var(--dk-text-primary, #374151); margin-bottom: 12px; }
.doc-feedback-btns { display: flex; justify-content: center; gap: 10px; }
.doc-fb-btn {
  background: var(--dk-bg-page, #f8f9fb);
  border: 1.5px solid var(--dk-border, #eaecf0);
  border-radius: 8px;
  padding: 7px 20px;
  cursor: pointer;
  font-size: 13px;
  font-family: inherit;
  transition: border-color .15s, background .15s;
}
.doc-fb-btn:hover { border-color: #E86A10; background: #fff5ee; }
.doc-fb-btn.active { border-color: #E86A10; background: #E86A10; color: #fff; }
.doc-feedback-thanks { margin-top: 10px; font-size: 13px; color: var(--dk-text-secondary, #6b7280); }

/* Overlay mobile */
.doc-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.4);
  z-index: 49;
}

/* ── Responsivo ─────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .doc-sidebar {
    position: fixed;
    left: -280px;
    top: 0;
    bottom: 0;
    z-index: 50;
    transition: left .25s;
    width: 270px;
    box-shadow: 4px 0 20px rgba(0,0,0,.15);
  }
  .doc-sidebar-open { left: 0; }
  .doc-overlay { display: block; }
  .doc-mobile-header { display: flex; }
  .doc-home { padding: 0 16px 40px; }
  .doc-article { padding: 20px 16px 40px; }
  .doc-home-hero { padding: 24px; }
  .doc-home-title { font-size: 20px; }
}
</style>
